import { PayloadAction } from '@reduxjs/toolkit';
import { PillVariant } from 'fe-design-base/molecules/Pill/Pill';
import { RootState } from 'initializers/types';

import { formatDate, getTimeDifferenceInHours } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import {
  ApplicationStage,
  ApplicationWithApplicant,
} from './interfaces/IApplication';
import { CompanyBusinessProfile } from './interfaces/ICompany';
import { UpcomingInterview } from './interfaces/IJobInterview';
import {
  JobRequest,
  JobRequestWeekAvailability,
} from './interfaces/IJobRequest';
import { NylasOpenHour } from './types/nylas';
import {
  HiringSliceReducers,
  HiringSliceSelectors,
  WithLoadingAndError,
} from './types/slice';
import {
  AGG_JOB_REQUEST_STATUS,
  APPLICATION_SOURCE_MAP,
  INTERVIEW_STATUS,
  JOB_REQUEST_STATUS,
} from './constants';

export const createReducers = <S extends Record<string, unknown>>(
  initialState: S
): HiringSliceReducers<S> => {
  const reducers = {} as HiringSliceReducers<S>;

  (Object.keys(initialState) as (keyof S)[]).forEach(key => {
    const capitalized = `${String(key).charAt(0).toUpperCase()}${String(
      key
    ).slice(1)}` as Capitalize<string & keyof S>;
    const actionType = `set${capitalized}` as keyof HiringSliceReducers<S>;

    reducers[actionType] = ((
      state: S,
      action: PayloadAction<S[typeof key]>
    ) => {
      state[key] = action.payload;
    }) as HiringSliceReducers<S>[typeof actionType];
  });

  return reducers;
};

export const createSelectors = <
  S extends WithLoadingAndError<Record<string, unknown>>
>(
  sliceState: (state: RootState) => S,
  initialState: S
): HiringSliceSelectors<S> => {
  const selectors = {} as HiringSliceSelectors<S>;

  (Object.keys(initialState) as (keyof S)[]).forEach(key => {
    const stringKey = key as string;
    const capitalized = `${stringKey.charAt(0).toUpperCase()}${stringKey.slice(
      1
    )}` as Capitalize<string & keyof S>;
    const actionType = `select${capitalized}` as keyof HiringSliceSelectors<S>;

    selectors[actionType] = ((state: RootState) =>
      sliceState(state)[key]) as HiringSliceSelectors<S>[typeof actionType];
  });

  return selectors;
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const mapJobRequestStatusToAggStatus = (
  status: string | undefined
): AGG_JOB_REQUEST_STATUS => {
  switch (status) {
    case JOB_REQUEST_STATUS.ACTIVE:
      return AGG_JOB_REQUEST_STATUS.LIVE;
    case JOB_REQUEST_STATUS.DRAFT:
      return AGG_JOB_REQUEST_STATUS.DRAFT;
    case JOB_REQUEST_STATUS.PENDING_REVIEW:
      return AGG_JOB_REQUEST_STATUS.IN_REVIEW;
    case JOB_REQUEST_STATUS.FLAGGED:
      return AGG_JOB_REQUEST_STATUS.IN_REVIEW;
    case JOB_REQUEST_STATUS.ARCHIVED:
      return AGG_JOB_REQUEST_STATUS.CLOSED;
    case JOB_REQUEST_STATUS.CLOSED:
      return AGG_JOB_REQUEST_STATUS.CLOSED;
    case JOB_REQUEST_STATUS.EXPIRED:
      return AGG_JOB_REQUEST_STATUS.CLOSED;
    default:
      return AGG_JOB_REQUEST_STATUS.IN_REVIEW;
  }
};

export const isJobRequestLive = (status: string | undefined) =>
  mapJobRequestStatusToAggStatus(status) === AGG_JOB_REQUEST_STATUS.LIVE;

export const isJobRequestDraft = (status: string | undefined) =>
  mapJobRequestStatusToAggStatus(status) === AGG_JOB_REQUEST_STATUS.DRAFT;

export const isJobRequestClosed = (status: string | undefined) =>
  mapJobRequestStatusToAggStatus(status) === AGG_JOB_REQUEST_STATUS.CLOSED;

export const isJobRequestInReview = (status: string | undefined) =>
  mapJobRequestStatusToAggStatus(status) === AGG_JOB_REQUEST_STATUS.IN_REVIEW;

export const convertJobRequestStatusToDisplayText = (
  status: string | undefined
): string => {
  const jobRequestStatus = mapJobRequestStatusToAggStatus(status);
  switch (jobRequestStatus) {
    case AGG_JOB_REQUEST_STATUS.LIVE:
      return toI18n('hiring.job_posts_review.status.live');
    case AGG_JOB_REQUEST_STATUS.DRAFT:
      return toI18n('hiring.job_posts_review.status.draft');
    case AGG_JOB_REQUEST_STATUS.IN_REVIEW:
      return toI18n('hiring.job_posts_review.status.in_review');
    case AGG_JOB_REQUEST_STATUS.CLOSED:
      return toI18n('hiring.job_posts_review.status.closed');
    default:
      return toI18n('hiring.job_posts_review.status.in_review');
  }
};

export const getPillVariantForJobRequestStatus = (
  status: string | undefined
): PillVariant => {
  const jobRequestStatus = mapJobRequestStatusToAggStatus(status);
  switch (jobRequestStatus) {
    case AGG_JOB_REQUEST_STATUS.LIVE:
      return 'success';
    case AGG_JOB_REQUEST_STATUS.DRAFT:
      return 'primary';
    case AGG_JOB_REQUEST_STATUS.IN_REVIEW:
      return 'info';
    case AGG_JOB_REQUEST_STATUS.CLOSED:
      return 'mono';
    default:
      return 'primary';
  }
};

export const isWeekend = (days: string[]) =>
  days.includes('saturday') && days.includes('sunday') && days.length === 2;

export const isWeekday = (days: string[]) =>
  days.length === 5 && !isWeekend(days);

export const isWeekdayAndWeekend = (days: string[]) => days.length > 5;

export const formatJobRequirementDays = (
  weekAvailabilities: JobRequestWeekAvailability | undefined
) => {
  if (!weekAvailabilities) return '';
  const days = Object.keys(weekAvailabilities).filter(
    day =>
      weekAvailabilities[day as keyof JobRequestWeekAvailability].length > 0
  );
  if (isWeekend(days))
    return toI18n(
      'hiring.v2.job_request.job_description_page.job_details.weekends'
    );
  if (isWeekday(days))
    return toI18n(
      'hiring.v2.job_request.job_description_page.job_details.weekdays'
    );
  if (isWeekdayAndWeekend(days))
    return `${toI18n(
      'hiring.v2.job_request.job_description_page.job_details.weekdays'
    )} & ${toI18n(
      'hiring.v2.job_request.job_description_page.job_details.weekends'
    )}`;
  return `${days
    .map(day =>
      capitalize(
        toI18n(`hiring.v2.job_request.job_description_page.job_details.${day}`)
      )
    )
    .join(', ')}`;
};

export const formatJobRequirementShifts = (
  weekAvailabilities: JobRequestWeekAvailability | undefined
) => {
  if (!weekAvailabilities) return '';
  const dayWithShifts = Object.keys(weekAvailabilities).find(
    day =>
      weekAvailabilities[day as keyof JobRequestWeekAvailability].length > 0
  );
  if (!dayWithShifts) return '';
  const shifts =
    weekAvailabilities[dayWithShifts as keyof JobRequestWeekAvailability];
  let formattedShifts = '';
  shifts.forEach(shift => {
    formattedShifts +=
      toI18n(
        `hiring.v2.job_request.job_description_page.job_details.shift_${shift}`
      ) + ', ';
  });

  return formattedShifts.slice(0, -2);
};

export const formatJobRequirementYearsOfExperience = (years: string) => {
  if (years === '0')
    return toI18n(
      'hiring.v2.job_request.job_description_page.job_details.no_experience_required'
    );
  return toI18n(
    'hiring.v2.job_request.job_description_page.job_details.years_of_experience_required',
    { props: { years } }
  );
};

export const getJobRequirementsTitle = (jobRequest: JobRequest) => {
  if (!jobRequest.employment_type) return '';
  return (
    jobRequest.employment_type +
    (jobRequest.week_availabilities?.monday?.length
      ? ', ' +
        formatJobRequirementDays(jobRequest.week_availabilities) +
        ', ' +
        formatJobRequirementShifts(jobRequest.week_availabilities)
      : '') +
    (jobRequest.years_experience
      ? ', ' +
        formatJobRequirementYearsOfExperience(jobRequest.years_experience)
      : '')
  );
};

const formatJobCompensationRangeFrequency = (frequency: string) => {
  if (frequency === 'hourly')
    return toI18n('hiring.v2.job_request.job_description_page.job_pay.hourly');
  if (frequency === 'salary')
    return toI18n('hiring.v2.job_request.job_description_page.job_pay.salary');
  return frequency;
};

const formatJobCompensationRange = (
  min: number,
  max: number | undefined | null,
  frequency: string | undefined
) => {
  const formattedFrequency = formatJobCompensationRangeFrequency(
    frequency || 'hourly'
  );
  if (!max || min === max) return `$${min}/${formattedFrequency}`;
  return `$${min} - $${max}/${formattedFrequency}`;
};

const formatJobCompensationTipsFrequency = (frequency: string) =>
  toI18n(`hiring.v2.job_request.job_description_page.job_pay.${frequency}`);

export const formatJobCompensationTips = (
  tip_amount: number | undefined,
  tip_frequency: string | undefined
) => {
  if (!tip_amount || tip_amount === 0) return '';
  const formattedFrequency = formatJobCompensationTipsFrequency(
    tip_frequency || ''
  );
  return toI18n(
    'hiring.v2.job_request.job_description_page.job_pay.tips_amount',
    { props: { tips_amt: tip_amount, tips_freq: formattedFrequency } }
  );
};

export const formatJobCompensationBenefits = (benefits: string | undefined) => {
  if (!benefits) return '';
  return toI18n(
    'hiring.v2.job_request.job_description_page.job_pay.benefits_included'
  );
};

export const getJobCompensationTitle = (
  compensation: JobRequest['job_compensation']
) => {
  if (!compensation?.target_wage_rate) return '';
  return (
    formatJobCompensationRange(
      compensation.target_wage_rate,
      compensation.target_wage_rate_max,
      compensation.wage_type
    ) +
    (compensation.tip_amount
      ? ', $' +
        formatJobCompensationTips(
          compensation.tip_amount,
          compensation.tip_frequency
        )
      : '') +
    (compensation.comments
      ? ', ' + formatJobCompensationBenefits(compensation.comments)
      : '')
  );
};

export const getJobDescriptionPromptText = (
  jobRole: string,
  type = 'part-time'
) =>
  toI18n(`hiring.v2.job_request.prompt_page.helper_text`, {
    props: {
      job_role: jobRole || 'Dishwasher',
      type,
    },
  });

export const getJobDescriptionChipText = (jobRole = 'Dishwasher') =>
  toI18n(`hiring.v2.job_request.prompt_page.helper_text_chip`, {
    props: { job_role: jobRole },
  });

export const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

export const getApplicantUniqueQualities = (
  qualities: { quote: string; rating: string; tag: string }[]
) => {
  if (!qualities) return [];
  const uniqueQualities: { tag: string; rating: string }[] = [];

  if (!!qualities) {
    const uniqueTags: Set<string> = new Set();
    for (const quality of qualities) {
      if (!uniqueTags.has(quality.tag)) {
        uniqueTags.add(quality.tag);
        uniqueQualities.push({ tag: quality.tag, rating: quality.rating });
      }
    }
  }

  return uniqueQualities;
};

export const ratingToVariant = (rating: string) => {
  switch (rating) {
    case 'positive':
      return 'success';
    case 'neutral':
      return 'mono';
    case 'negative':
      return 'mono';
    default:
      return 'mono';
  }
};

export const getCompanyAddressFromBusinessProfile = (
  profile: CompanyBusinessProfile | null
) => {
  if (!profile) return '';
  let address = '';
  if (profile.street_number) address += `${profile.street_number} `;
  if (profile.street_name) address += `${profile.street_name}, `;
  if (profile.city) address += `${profile.city}, `;
  if (profile.state) address += `${profile.state} `;
  if (profile.zip) address += `${profile.zip}`;
  return address;
};

export const convertInterviewStatusToDisplayText = (
  status: string | undefined
): string => {
  switch (status) {
    case INTERVIEW_STATUS.CONFIRMED:
      return toI18n('hiring.v2.interview_status.confirmed');
    case INTERVIEW_STATUS.NEW_TIME_PROPOSED:
      return toI18n('hiring.v2.interview_status.new_time_proposed');
    case INTERVIEW_STATUS.NOT_CONFIRMED:
      return toI18n('hiring.v2.interview_status.not_confirmed');
    default:
      return status || '';
  }
};

export const getPillVariantForInterviewStatus = (
  status: string | undefined
): PillVariant => {
  switch (status) {
    case INTERVIEW_STATUS.CONFIRMED:
      return 'success';
    case INTERVIEW_STATUS.NOT_CONFIRMED:
      return 'warning';
    case INTERVIEW_STATUS.NEW_TIME_PROPOSED:
      return 'error';
    default:
      return 'info';
  }
};

export const getTotalOpenHours = (openHours: NylasOpenHour[]) => {
  let totalHours = 0;

  openHours.forEach(({ start, end, days }) => {
    const duration = getTimeDifferenceInHours(start, end, 'HH:mm');

    // Handle overnight spans
    const normalizedDuration = duration < 0 ? duration + 24 : duration;

    totalHours += normalizedDuration * days.length;
  });

  return totalHours;
};

export const getApplicationStage = (
  application: ApplicationWithApplicant | null
): ApplicationStage => {
  if (!application) return 'ai_screener';
  switch (application.status) {
    case 'new':
      return 'ai_screener';
    case 'candidate_requested_interview':
      return 'interview';
    case 'interview_schedule_link_sent':
      return 'interview';
    case 'oam_confirmed_interview':
      return 'interview';
    case 'not_interested':
      return 'decline';
    case 'interview_declined':
      return 'decline';
    case 'added_to_team':
      return 'hire';
    default:
      return 'ai_screener';
  }
};

export const getApplicationStageDisplayText = (
  application: ApplicationWithApplicant | null
) => {
  const stage = getApplicationStage(application);
  switch (stage) {
    case 'ai_screener':
      return toI18n('hiring.candidates_review.stage.ai_screener');
    case 'interview':
      return toI18n('hiring.candidates_review.stage.interview');
    case 'hire':
      return toI18n('hiring.candidates_review.stage.hire');
    case 'decline':
      return toI18n('hiring.candidates_review.stage.decline');
    default:
      return toI18n('hiring.candidates_review.stage.ai_screener');
  }
};

export const APPLICATION_STATUS_PILL_MAP = {
  interview_declined: {
    variant: 'mono',
    message: toI18n('hiring.candidates_review.status.sent_decline_note'),
  },
  not_interested: {
    variant: 'mono',
    message: toI18n('hiring.candidates_review.status.sent_decline_note'),
  },
  interview_reschedule_request: {
    variant: 'error',
    message: toI18n(
      'hiring.candidates_review.status.interview_reschedule_request'
    ),
  },
  completed: {
    variant: 'error',
    message: toI18n('hiring.candidates_review.status.needs_decision'),
  },
  candidate_picked_from_link: {
    variant: 'error',
    message: toI18n('hiring.candidates_review.status.confirm_interview_time'),
  },
  candidate_requested_interview: {
    variant: 'error',
    message: toI18n('hiring.candidates_review.status.needs_review'),
  },
  oam_confirmed_interview: {
    variant: 'success',
    message: toI18n('hiring.candidates_review.status.interview_scheduled'),
  },
  added_to_team: {
    variant: 'success',
    message: toI18n('hiring.candidates_review.status.added_to_team'),
  },
  hasn_t_completed_screener: {
    variant: 'mono',
    message: toI18n(
      'hiring.candidates_review.status.hasn_t_completed_screener'
    ),
  },
  needs_review: {
    variant: 'error',
    message: toI18n('hiring.candidates_review.status.needs_review'),
  },
  booking_link_sent: {
    variant: 'mono',
    message: toI18n('hiring.candidates_review.status.booking_link_sent'),
  },
  maybe_later: {
    variant: 'mono',
    message: toI18n('hiring.candidates_review.status.maybe_later'),
  },
} as const;

export const getApplicationStatusDisplayText = (
  application: ApplicationWithApplicant | null
): { variant: PillVariant; message: string } => {
  if (!application) return APPLICATION_STATUS_PILL_MAP.needs_review;
  const applicationStatus = application.status || '';
  const interview = application.hiring_interviews?.[0] || null;
  const interviewStatus = interview?.status || '';
  const screenerCompleted =
    !!application?.applicant?.profile_data?.highlights?.length;

  // The order of the conditions is important. It is based on priority.

  // Priority 0: These represent negative statuses
  if (applicationStatus === 'interview_declined')
    return APPLICATION_STATUS_PILL_MAP.interview_declined;
  if (applicationStatus === 'not_interested')
    return APPLICATION_STATUS_PILL_MAP.not_interested;

  // Priority 1: OAM added candidate to team
  if (applicationStatus === 'added_to_team')
    return APPLICATION_STATUS_PILL_MAP.added_to_team;

  // Priority 2: Interview has been rescheduled
  if (interviewStatus === 'reschedule_requested')
    return APPLICATION_STATUS_PILL_MAP.interview_reschedule_request;

  // Priority 3: OAM confirmed interview time
  if (applicationStatus === 'oam_confirmed_interview')
    return APPLICATION_STATUS_PILL_MAP.oam_confirmed_interview;

  // Priority 4: Interview has been completed
  if (interviewStatus === 'completed')
    return APPLICATION_STATUS_PILL_MAP.completed;

  // Priority 5: Candidate selects a timeslot from
  // the booking link sent to them by the OAM
  if (
    interviewStatus === 'accepted' &&
    applicationStatus === 'interview_schedule_link_sent'
  )
    return APPLICATION_STATUS_PILL_MAP.candidate_picked_from_link;

  // Priority 6: Booking link sent to candidate
  if (applicationStatus === 'interview_schedule_link_sent')
    return APPLICATION_STATUS_PILL_MAP.booking_link_sent;

  // Priority 7: Candidate requested interview
  if (applicationStatus === 'candidate_requested_interview')
    return APPLICATION_STATUS_PILL_MAP.candidate_requested_interview;

  // Priority 8: Candidate hasn't completed the screener
  if (!interview && !screenerCompleted)
    return APPLICATION_STATUS_PILL_MAP.hasn_t_completed_screener;

  // Default: Priority 9: Candidate needs review
  return APPLICATION_STATUS_PILL_MAP.needs_review;
};

export const isOamBusinessProfileComplete = (
  businessProfile: CompanyBusinessProfile | null
) => {
  if (!businessProfile) return false;
  if (!businessProfile.street_number) return false;
  if (!businessProfile.street_name) return false;
  if (!businessProfile.city) return false;
  if (!businessProfile.state) return false;
  if (!businessProfile.zip) return false;
  if (!businessProfile.phone) return false;
  if (!businessProfile.email) return false;
  if (!businessProfile.country_code) return false;
  return true;
};

export const getJobPostInfoTooltipText = (jobPost: JobRequest | null) => {
  if (!jobPost) return '';
  const status = mapJobRequestStatusToAggStatus(jobPost.status);
  switch (status) {
    case AGG_JOB_REQUEST_STATUS.LIVE:
      return toI18n('hiring.job_posts_review.card.live_job', {
        props: { date: formatDate(jobPost.expires_at, 'pretty_short_day') },
      });
    case AGG_JOB_REQUEST_STATUS.DRAFT:
      return toI18n('hiring.job_posts_review.card.draft_job');
    case AGG_JOB_REQUEST_STATUS.IN_REVIEW:
      return toI18n('hiring.job_posts_review.card.in_review_job');
    case AGG_JOB_REQUEST_STATUS.CLOSED:
      return toI18n('hiring.job_posts_review.card.closed_job');
    default:
      return '';
  }
};

export const groupInterviewsByDay = (
  interviews: UpcomingInterview[]
): { date: string; interviews: UpcomingInterview[] }[] => {
  if (!interviews.length) return [];
  const groupedInterviews = interviews.reduce(
    (acc: Record<string, UpcomingInterview[]>, interview) => {
      const date = new Date(interview.start_at);
      const dateKey = date.toISOString().split('T')[0];
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(interview);
      return acc;
    },
    {} as Record<string, UpcomingInterview[]>
  );
  return Object.entries(groupedInterviews).map(([date, interviews]) => ({
    date,
    interviews,
  }));
};

/**
 * Normalizes and returns a display-friendly application source text.
 *
 * The database contains many legacy/malformed source values including:
 * - Typos (Facebok, Text_to_applu)
 * - Partial values (Homebase Em, QR Po)
 * - Mixed case (indeed, Indeed, INDEED)
 * - URLs appended (QR Posterhttps://..., Indeedhttps://...)
 * - Special characters and trailing punctuation
 * - XSS-like attempts ("<marquee>")
 * - Numeric values (1, 2)
 * - NULL values
 *
 * This function normalizes all these variations to clean display values.
 */
export const getApplicationSourceDisplayText = (
  source: string | null | undefined
): string => {
  // Handle null/undefined/empty
  if (!source || source === 'NULL') return '';

  // First, check for exact match (most common case)
  if (source in APPLICATION_SOURCE_MAP) {
    return APPLICATION_SOURCE_MAP[
      source as keyof typeof APPLICATION_SOURCE_MAP
    ];
  }

  const formattedSource = source.toLowerCase().trim();

  // Handle indeed variants
  if (formattedSource.includes('indeed')) return APPLICATION_SOURCE_MAP.indeed;

  // Handle ZipRecruiter variants
  if (formattedSource.includes('zip'))
    return APPLICATION_SOURCE_MAP.zip_recruiter;

  // Handle linkedin variants
  if (formattedSource.includes('linked'))
    return APPLICATION_SOURCE_MAP.linked_in;

  // Handle instagram variants
  if (formattedSource.includes('insta'))
    return APPLICATION_SOURCE_MAP.instagram;

  // Handle facebook variants
  if (formattedSource.includes('face')) return APPLICATION_SOURCE_MAP.facebook;

  // Handle twitter variants
  if (formattedSource.includes('twitter'))
    return APPLICATION_SOURCE_MAP.twitter;

  // Handle craigslist variants
  if (formattedSource.includes('craigs'))
    return APPLICATION_SOURCE_MAP.craigslist;

  // Handle qr poster variants
  if (formattedSource.includes('qr')) return APPLICATION_SOURCE_MAP.qr_poster;

  // Handle google variants
  if (formattedSource.includes('google')) return APPLICATION_SOURCE_MAP.google;

  // Handle talroo variants
  if (formattedSource.includes('talroo')) return APPLICATION_SOURCE_MAP.talroo;

  // Handle referral variants
  if (formattedSource.includes('refer'))
    return APPLICATION_SOURCE_MAP.referral_message;

  // Handle walk-ins feed variants
  if (formattedSource.includes('_to')) return APPLICATION_SOURCE_MAP.walk_in;

  // Handle handshake variants
  if (formattedSource.includes('handshake'))
    return APPLICATION_SOURCE_MAP.handshake;

  // Handle homebase variants
  if (formattedSource.includes('homebase'))
    return APPLICATION_SOURCE_MAP.homebase;

  return APPLICATION_SOURCE_MAP.generic_feed;
};
